<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { Search01Icon, Cancel01Icon } from "@/components/icons";
import { useGlobalSearch, CATEGORY_CONFIG, type SearchResult } from "@/composables/useGlobalSearch";

const {
	query,
	isOpen,
	selectedIndex,
	groupedResults,
	flatResults,
	hasResults,
	moveUp,
	moveDown,
	selectCurrent,
	navigateTo,
	open,
	close,
} = useGlobalSearch();

const inputRef = ref<HTMLInputElement | null>(null);
const containerRef = ref<HTMLElement | null>(null);

const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
const shortcut = isMac ? "⌘K" : "Ctrl K";

/* ── Focus input ── */

function focusInput() {
	const el = inputRef.value;
	if (el) {
		el.focus();
		open();
	}
}

/* ── Cmd+K / Ctrl+K global shortcut ── */

function onGlobalKeydown(e: KeyboardEvent) {
	if ((e.metaKey || e.ctrlKey) && e.key === "k") {
		e.preventDefault();
		focusInput();
	}
}

/* ── Click outside to close ── */

function onClickOutside(e: MouseEvent) {
	if (containerRef.value && !containerRef.value.contains(e.target as Node)) {
		close();
	}
}

onMounted(() => {
	document.addEventListener("keydown", onGlobalKeydown);
	document.addEventListener("mousedown", onClickOutside);
});

onUnmounted(() => {
	document.removeEventListener("keydown", onGlobalKeydown);
	document.removeEventListener("mousedown", onClickOutside);
});

/* ── Helpers ── */

function isSelected(result: SearchResult) {
	return flatResults.value[selectedIndex.value] === result;
}

function onInputFocus() {
	if (query.value.length >= 2) {
		open();
	}
}

function onInput() {
	if (query.value.length >= 2) {
		open();
	}
}

function clear() {
	query.value = "";
	close();
	inputRef.value?.focus();
}
</script>

<template>
	<div class="hidden md:block relative w-60 xl:w-72 shrink-0" ref="containerRef">
		<Search01Icon :size="14" class="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-3 pointer-events-none" />
		<input
			ref="inputRef"
			v-model="query"
			type="search"
			class="input pl-8 pr-14 [&::-webkit-search-cancel-button]:hidden"
			placeholder="Search PrintHQ"
			aria-label="Search PrintHQ"
			autocomplete="off"
			@focus="onInputFocus"
			@input="onInput"
			@keydown.escape="close"
			@keydown.down.prevent="moveDown"
			@keydown.up.prevent="moveUp"
			@keydown.enter.prevent="selectCurrent"
		/>
		<button
			v-if="query"
			class="absolute right-1.5 top-1/2 -translate-y-1/2 icon-btn-sm size-6"
			aria-label="Clear search"
			title="Clear search"
			@click="clear"
		>
			<Cancel01Icon :size="13" />
		</button>
		<span v-else class="kbd absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">{{ shortcut }}</span>

		<!-- Results -->
		<div
			v-if="isOpen && query.length >= 2"
			class="menu absolute left-0 top-full mt-1.5 w-[420px] max-h-[70vh] overflow-y-auto z-50"
			role="listbox"
			aria-label="Search results"
		>
			<template v-for="[category, results] in groupedResults" :key="category">
				<div class="section-title px-2.5 pt-2 pb-1">
					{{ CATEGORY_CONFIG[category].label }}
				</div>
				<button
					v-for="result in results"
					:key="result.id"
					class="menu-item"
					:class="{ 'bg-(--menu-hover)': isSelected(result) }"
					role="option"
					:aria-selected="isSelected(result)"
					@click="navigateTo(result)"
					@mouseenter="selectedIndex = flatResults.indexOf(result)"
				>
					<component :is="CATEGORY_CONFIG[category].icon" class="size-4 text-fg-3 shrink-0" />
					<span class="min-w-0 flex-1">
						<span class="block truncate font-medium">{{ result.label }}</span>
						<span v-if="result.subtitle" class="block truncate text-[11px] text-fg-3">{{ result.subtitle }}</span>
					</span>
				</button>
			</template>

			<div v-if="!hasResults" class="px-4 py-6 text-center">
				<p class="text-[13px] font-medium">Nothing matches “{{ query }}”</p>
				<p class="text-xs text-fg-3 mt-1">Try a printer, filament, project or file name.</p>
			</div>
		</div>
	</div>
</template>
